import React, { useCallback, useState } from "react";
import { useAppDispatch, useAppSelector } from "store";
import { SnackbarKey, useSnackbar } from "notistack";
import { useEffect } from "react";
import { removeNotification } from "features/notifications/notificationsSlice";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import AppRouter from "router";
import { LayoutGroup } from "framer-motion";
import { Helmet } from "react-helmet";
import { useIdleTimer } from "react-idle-timer";
import { logout } from "service/api";
import { useAccountQuery } from "features/auth/authApi";
import { Tooltip } from "@mui/material";
import { useSite } from "hooks/use-site";
import i18n from "i18next";

const App = () => {
  const [count, setCount] = useState<number>(0);
  const { favicon } = useSite();
  useEffect(() => {
    const setFavicon = (iconUrl: string) => {
      const link = document.querySelector(
        "link[rel='icon']"
      ) as HTMLLinkElement | null;

      if (link) {
        link.href = iconUrl;
      } else {
        const newLink = document.createElement("link");
        newLink.rel = "icon";
        newLink.href = iconUrl;
        document.head.appendChild(newLink);
      }
    };
    setFavicon(favicon);
  }, [favicon]);

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { multilanguage } = useSite();
  const notifications = useAppSelector((state) => state.notifications.items);

  const { data: account } = useAccountQuery(undefined, {
    pollingInterval: 60000,
  });

  const idleLogoutTimeout =
    account && account.user ? account.user.idle_logout_time : 15;

  const onAction = () => {
    setCount(count + 1);
  };

  const onIdle = () => {
    logout().finally(() => {
      window.location.reload();
    });
  };

  useIdleTimer({
    onIdle,
    onAction,
    crossTab: true,
    leaderElection: true,
    syncTimers: 200,
    timeout: Number(idleLogoutTimeout) * 60 * 1000,
  });

  const dispatch = useAppDispatch();

  const handleCloseSnackbar = useCallback(
    (key: SnackbarKey) => () => {
      closeSnackbar(key);
    },
    [closeSnackbar]
  );

  useEffect(() => {
    if (account && account.user && !account.user.is_active) {
      logout().finally(() => {
        window.location.reload();
      });
    }
  }, [account]);

  useEffect(() => {
    if (!account?.user) return;

    const forceEnglish = localStorage.getItem("forceEnglish") === "1";
    if (forceEnglish) {
      i18n.changeLanguage("en");
      localStorage.setItem("lang", "en");
      return;
    }

    const userLang = account.user.ui_language || "en";
    const savedLang = localStorage.getItem("lang");

    if (userLang && userLang !== savedLang) {
      localStorage.setItem("lang", userLang);
      i18n.changeLanguage(userLang);
    }
  }, [account?.user]);

  useEffect(() => {
    if (!multilanguage) {
      localStorage.setItem("forceEnglish", "1");
    } else {
      localStorage.removeItem("forceEnglish");
    }
  }, [multilanguage]);

  useEffect(() => {
    notifications.forEach((notification) => {
      enqueueSnackbar(notification.message, {
        variant: notification.type,
        autoHideDuration: 3000,
        action: (id) => (
          <Tooltip title="Dismiss">
            <IconButton color="white" onClick={handleCloseSnackbar(id)}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        ),
      });
      dispatch(removeNotification(notification.message));
    });
  }, [notifications, enqueueSnackbar, dispatch, handleCloseSnackbar]);

  return (
    <LayoutGroup>
      <AppRouter />
      <Helmet></Helmet>
    </LayoutGroup>
  );
};

export default App;
